import { useQuery } from '@tanstack/react-query'
import type { QueryClient } from '@tanstack/react-query'
import type { AdminListItem, SettingsResponse } from '@ayub/shared'
import { useAuthSession } from '@/components/auth/hooks'
import { fetchAdmins, fetchSettings } from './api'

export function useSettings() {
  const query = useQuery<SettingsResponse, Error>({
    queryKey: ['settings'],
    queryFn: fetchSettings,
  })

  return {
    data: query.data,
    isLoading: query.isLoading,
    isFetching: query.isFetching,
    error: query.isError ? query.error : null,
    refetch: query.refetch,
  }
}

export function useAdmins() {
  const query = useQuery<AdminListItem[], Error>({
    queryKey: ['admins'],
    queryFn: fetchAdmins,
  })

  return {
    items: query.data ?? [],
    isLoading: query.isLoading,
    isFetching: query.isFetching,
    error: query.isError ? query.error : null,
    refetch: query.refetch,
  }
}

export interface CurrentAdminInfo {
  /** Joriy admin super ekanmi. `isKnown: false` bo'lganda bu SAFE DEFAULT — "yo'q" deb
   * hisoblanadi (yozish tugmalari yopiq turadi), haqiqiy javob emas. */
  isSuper: boolean
  /** Aniqlash muvaffaqiyatli bo'ldimi. `false` — `GET /api/auth/me` hali javob bermagan
   * (yuklanmoqda) yoki xato qaytargan holatda. */
  isKnown: boolean
  /** Joriy adminning o'z `telegramId`si — "o'zini o'chira olmaydi" qoidasini UI'da oldindan
   * ushlash uchun (`AdminsSection.tsx`). Noma'lum bo'lsa `null`. */
  telegramId: number | null
}

/**
 * Joriy adminning super ekanligini aniqlash — `GET /api/auth/me` orqali.
 *
 * ESKI YECHIM (Bosqich 11, olib tashlandi): o'sha paytda tayyor auth kontekst YO'Q edi —
 * `AdminListItem`da "bu SIZ" degan alohida maydon yo'q, server `/api/auth/me` ham hali
 * qurilmagan edi. Shuning uchun `lib/telegram.ts`dagi `getTelegramUser()` bergan Telegram
 * ID `GET /api/admins` ro'yxati bilan QO'LDA solishtirilardi — bu VAQTINCHALIK yechim edi:
 * ro'yxat hali kelmagan bo'lsa yoki oddiy admin o'zini top qila olmasa, noto'g'ri javob
 * berishi mumkin edi.
 *
 * Endi server o'zi (Telegram initData yoki brauzer sessiya cookie'siga qarab, ikkalasi
 * ham `authHook` orqali) joriy adminni allaqachon aniqlagan — shunchaki shu javobning
 * `isSuper` maydoni o'qiladi, taxmin qilishga hojat yo'q.
 *
 * Javob hali kelmagan yoki xato bo'lsa `isKnown: false`, `isSuper: false` qaytadi — bu
 * FAQAT birinchi himoya qatlami (UI'da tugmalarni yashirish/o'chirish). Ikkinchi qatlam —
 * server har doim o'zi ham `requireSuperAdmin()` bilan tekshiradi (`routes/settings.ts`),
 * shuning uchun bu yerdagi noto'g'ri aniqlash XAVFSIZLIK teshigi emas, faqat noqulay UX
 * bo'lib qoladi (`SettingsPage.tsx`dagi 403 xato xabari — ikkinchi himoya qatlamining
 * natijasi shu yerda ko'rinadi).
 */
export function useCurrentAdmin(): CurrentAdminInfo {
  // O'ZINING `useQuery({ queryKey: ['auth', 'me'], ... })`sini yaratish O'RNIGA
  // `useAuthSession()` qayta ishlatiladi — ESKI kodda ikkalasi BIR XIL `queryKey`ga ega
  // bo'lsa-da, ikki xil `retry` siyosati bilan yozilgan edi (bu yerda standart retry,
  // `auth/hooks.ts`da 401'da to'xtaydigan retry) — TanStack Query v5'da bitta kalitning
  // OXIRGI ro'yxatdan o'tgan `queryFn`/sozlamalari BARCHA shu kalitni ishlatuvchi
  // komponentlar uchun amal qiladi (footgun): qaysi komponent oldinroq/keyinroq
  // render bo'lishiga qarab xatti-harakat o'zgarib qolishi mumkin edi. Endi MANBA BITTA.
  const session = useAuthSession()

  if (!session.me) {
    return { isSuper: false, isKnown: false, telegramId: null }
  }

  return { isSuper: session.me.isSuper, isKnown: true, telegramId: session.me.telegramId }
}

/**
 * Sozlama o'zgarishi stavka/chegara/kunlar hisobiga ta'sir qiladi — shuning uchun
 * `videos`/`payouts`/`dashboard` keshlari ham eskiradi (`payouts/hooks.ts`dagi
 * `invalidatePayoutRelatedCaches()` bilan bir xil naqsh). `bloggers` esa tegilmaydi:
 * bloger ro'yxatida pul summasi ko'rsatilsa ham, u hisoblanmagan (saqlangan) qiymat —
 * faqat allaqachon yopilgan videolar bo'yicha (`BloggerFinance` izohi).
 */
export function invalidateSettingsRelatedCaches(queryClient: QueryClient): void {
  void queryClient.invalidateQueries({
    predicate: (query) => {
      const key = query.queryKey[0]
      return key === 'settings' || key === 'videos' || key === 'payouts' || key === 'dashboard'
    },
  })
}
